import {FC, useEffect, useState} from 'react';
import {ReactComponent as ImportIcon} from '@icons/import.svg';
import {ReactComponent as SearchIcon} from '@icons/search-normal.svg';
import {BODY1, defaultPage, defaultPageSize, SUBTITLE1} from '@/utils/constants';
import {Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from '@mui/material';
import ActionsMenu from '@/shared/components/Table/ActionMenu';
import Pagination from '@/shared/components/Pagination';
import Input from '@/shared/components/Inputs/Input';
import Button from '@/shared/components/Button';
import {ReactComponent as ArrowUpDownIcon} from '@icons/arrow-up-down.svg';
import {ReportsModalTablesStyle} from '../UnpaidItemsModal/ReportsModalTables.style';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getCurrentEventSelector} from '@/store/organization-event';
import {OrganizationReportsService} from '@/utils/services/organization-reports.service';
import {downloadFile, getErrorMessage} from '@/utils/helpers';
import {toast} from 'react-toastify';
import {REPORTS_GET_FILE} from '@/utils/enums/organization-reports.enum';
import {getReportLoadingSelector, getReportUnpaidTables, getUnpaidTablesSelector} from '@/store/organization-report';
import {IUnpaidTablesData} from '@/utils/types/organization-reports.interface';
import Loader from '@/shared/components/Loader';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
import Modal from '@/shared/components/Modal';
import TableDetails from '@/components/Organization/TablesAndTickets/TableDetails';
import {tablesAndTicketsTableStyle} from '@/components/Organization/TablesAndTickets/TablesAndTicketsTable/TablesAndTicketsTable.styles';
import {updateTicketStatus} from '@/store/organization-tables';
import {getDashboardStatistics} from '@/store/organization-dashboard';
import {OrganizationStripeService} from '@/utils/services/organization-stripe.services';
import {getTeamSelector} from '@/store/organization';
import {ITeamRole} from '@/utils/enums';

const columnOptions = ['Table #', 'Table name', 'Type of seat', 'Table purchaser', 'Price', 'Status'];

const columnKeyMap: Record<string, keyof IUnpaidTablesData> = {
  'Table #': 'number',
  'Table name': 'name',
  'Type of seat': 'typeOfSeat',
  'Table purchaser': 'purchaser',
  Price: 'price',
  Status: 'status',
};

const UnpaidTablesModal: FC = () => {
  const dispatch = useAppDispatch();

  const currentEvent = useAppSelector(getCurrentEventSelector);
  const unpaidTablesList = useAppSelector(getUnpaidTablesSelector);
  const isLoading = useAppSelector(getReportLoadingSelector);
  const team = useAppSelector(getTeamSelector);

  const isStandartMember = !!team && team.role === ITeamRole.STANDART;

  const [searchName, setSearchName] = useState('');
  const [page, setPage] = useState<number>(defaultPage);
  const [filterValues, setFilterValues] = useState(unpaidTablesList?.data || []);
  const [sortState, setSortState] = useState<{column: keyof IUnpaidTablesData | null; direction: 'asc' | 'desc'}>({
    column: null,
    direction: 'asc',
  });
  const [filteredAllValues, setFilteredAllValues] = useState<IUnpaidTablesData[]>([]);
  const [isShowTicketDetailsModal, setIsShowTicketDetailsModal] = useState<boolean>(false);
  const [detailsTicketId, setDetailsTicketId] = useState<number>();
  const [isAccessModalOpen, setIsAccessModalOpen] = useState<boolean>(false);

  const handleAccessModalChange = () => setIsAccessModalOpen(!isAccessModalOpen);

  const handleOpenTicketDetailsModal = (tableId?: number) => {
    setIsShowTicketDetailsModal(!isShowTicketDetailsModal);
    if (!isShowTicketDetailsModal && tableId) setDetailsTicketId(tableId);
    if (isShowTicketDetailsModal) getUnpaidTablesData();
  };
  const getUnpaidTablesData = async () => {
    if (!currentEvent) return;

    try {
      const reqBody = {
        eventId: currentEvent?.id,
        page,
        searchName,
      };

      const {data} = await dispatch(getReportUnpaidTables(reqBody)).unwrap();

      setFilteredAllValues(data);
      setFilterValues(data.slice(0, defaultPageSize));
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const sortData = (column: keyof IUnpaidTablesData) => {
    const direction = sortState.column === column && sortState.direction === 'asc' ? 'desc' : 'asc';

    const sorted = [...filterValues].sort((a, b) => {
      const valA = a[column];
      const valB = b[column];

      if (typeof valA === 'number' && typeof valB === 'number') {
        return direction === 'asc' ? valA - valB : valB - valA;
      }

      return direction === 'asc' ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
    });

    setSortState({column, direction});
    setFilterValues(sorted);
  };

  const handleSearchChange = (name: string) => {
    setSearchName(name);
    setPage(1);
  };

  const handlePaginationChange = (step: number) => {
    setPage(step);

    const startIdx = (step - 1) * defaultPageSize;
    const endIdx = startIdx + defaultPageSize;
    setFilterValues(filteredAllValues.slice(startIdx, endIdx));
  };

  const handleDownloadCSV = async () => {
    if (!currentEvent) return;

    try {
      const file = await OrganizationReportsService.getUnpaidTablesCsv(currentEvent?.id);
      downloadFile(file.data, REPORTS_GET_FILE.UNPAID_TABLES);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleUpdateStatus = async ({id, isPaid}: {id: number; isPaid: boolean}) => {
    if (!currentEvent) return;
    try {
      await dispatch(updateTicketStatus({tableId: id, isPaid})).unwrap();
      await getUnpaidTablesData();
      await dispatch(getDashboardStatistics(currentEvent?.id)).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleMakePaid = (id: number) => {
    if (isStandartMember) {
      handleAccessModalChange();
      return;
    }
    handleUpdateStatus({id, isPaid: true});
  };

  const sendTablePaymentLink = async (attendeeId: number) => {
    if (!currentEvent) return;
    try {
      await OrganizationStripeService.sendPaymentLink(attendeeId, currentEvent?.id);
      toast.success('Payment link sent successfully');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const paginationText = unpaidTablesList?.totalCount
    ? `Showing ${(page - 1) * defaultPageSize + 1} - ${Math.min(
        page * defaultPageSize,
        unpaidTablesList?.totalCount ?? 0
      )} of ${unpaidTablesList?.totalCount} items`
    : 'Showing 0 items';

  useEffect(() => {
    if (!unpaidTablesList?.data) return;

    setFilteredAllValues(unpaidTablesList?.data);
    setFilterValues(unpaidTablesList?.data.slice(0, defaultPageSize));
  }, []);

  useEffect(() => {
    getUnpaidTablesData();
  }, [currentEvent, page, searchName]);

  return (
    <Box sx={ReportsModalTablesStyle.wrapper}>
      <Box sx={ReportsModalTablesStyle.filterRow}>
        <Typography variant={SUBTITLE1} sx={ReportsModalTablesStyle.title}>
          {paginationText}
        </Typography>

        <Box sx={ReportsModalTablesStyle.search}>
          <SearchIcon />
          <Input placeholder="Search" value={searchName} onChange={handleSearchChange} />
        </Box>
        <Box>
          <Button
            sx={ReportsModalTablesStyle.download}
            startIcon={<ImportIcon />}
            onClick={handleDownloadCSV}
            title="Download.csv"
          />
        </Box>
      </Box>
      {isLoading ? (
        <Loader parentClassName={ReportsModalTablesStyle.loader} size={50} />
      ) : filterValues && filterValues.length ? (
        <Box sx={ReportsModalTablesStyle.table}>
          <TableContainer>
            <Table sx={ReportsModalTablesStyle.tableStyle} aria-label="simple table">
              <TableHead>
                <TableRow sx={{height: '32px'}}>
                  {columnOptions.map((col) => {
                    const sortable = col === 'Table #' || col === 'Table purchaser' || col === 'Price';

                    return (
                      <TableCell
                        className={`${col}`}
                        key={col}
                        onClick={() => sortable && sortData(columnKeyMap[col] as keyof IUnpaidTablesData)}
                        sx={{
                          cursor: sortable ? 'pointer' : 'default',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        <Box sx={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                          {col}
                          <Box sx={{width: '20px', display: 'flex', alignItems: 'center'}}>
                            {sortable ? <ArrowUpDownIcon /> : null}
                          </Box>
                        </Box>
                      </TableCell>
                    );
                  })}
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {filterValues &&
                  filterValues.map((row, index) => (
                    <TableRow key={index} sx={{padding: '11px 0px'}}>
                      {columnOptions.map((col) => {
                        const fieldKey = columnKeyMap[col];

                        if (col === 'Status') {
                          return (
                            <TableCell key={col} className={col}>
                              <Box sx={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                <Typography className={row.status}>{row.status}</Typography>
                                <Box
                                  sx={{width: '5px', height: '5px', borderRadius: '50%'}}
                                  className={`circle-${row.status}`}
                                />
                              </Box>
                            </TableCell>
                          );
                        }
                        if (col === 'Table purchaser') {
                          return (
                            <TableCell key={col} className="itemMaxBid">
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'flex-start',
                                  py: '8px',
                                  gap: '16px',
                                }}
                              >
                                <Box>
                                  <Typography sx={{fontSize: '14px', fontWeight: '700 !important'}}>
                                    {row.purchaser}
                                  </Typography>
                                  <Typography sx={{color: 'var(--neutral-300) !important'}}>{row.email}</Typography>
                                </Box>
                              </Box>
                            </TableCell>
                          );
                        }

                        if (fieldKey) {
                          const value = row[fieldKey];

                          return (
                            <TableCell key={col} className={col}>
                              <Typography
                                sx={{
                                  color:
                                    typeof value === 'number' && fieldKey === 'price'
                                      ? 'var(--error-700) !important'
                                      : 'var(--neutral-900) !important',
                                }}
                              >
                                {typeof value === 'number' && fieldKey === 'price' ? `-$${value}` : value || '-'}
                              </Typography>
                            </TableCell>
                          );
                        }

                        return (
                          <TableCell key={col} className={col}>
                            -
                          </TableCell>
                        );
                      })}
                      <TableCell sx={{textAlign: 'right', whiteSpace: 'nowrap'}}>
                        <ActionsMenu
                          content={
                            <Box sx={ReportsModalTablesStyle.menu}>
                              <Typography
                                sx={ReportsModalTablesStyle.menuContent}
                                onClick={() => {
                                  handleOpenTicketDetailsModal(row.id);
                                }}
                                variant={BODY1}
                              >
                                Go to Table Details
                              </Typography>
                              <Typography
                                sx={ReportsModalTablesStyle.menuContent}
                                onClick={() => {
                                  sendTablePaymentLink(row.purchaserAttendeeId);
                                }}
                                variant={BODY1}
                              >
                                Send payment link
                              </Typography>
                              <Typography
                                sx={ReportsModalTablesStyle.menuContent}
                                onClick={() => handleMakePaid(row.id)}
                                variant={BODY1}
                              >
                                Mark as Paid
                              </Typography>
                            </Box>
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={ReportsModalTablesStyle.paginationContainer}>
            <Pagination
              page={page}
              pageSize={defaultPageSize}
              total={unpaidTablesList?.totalCount || 0}
              onChange={(step) => handlePaginationChange(step)}
            />
          </Box>
        </Box>
      ) : (
        <EmptyTableData />
      )}

      <Modal
        onClose={handleOpenTicketDetailsModal}
        open={isShowTicketDetailsModal}
        sx={tablesAndTicketsTableStyle.modal}
      >
        <TableDetails onCloseModal={handleOpenTicketDetailsModal} currentTableId={detailsTicketId} />
      </Modal>
      <Modal
        open={isAccessModalOpen}
        onClose={handleAccessModalChange}
        title="Access Denied"
        description="You do not have access to this feature"
        sx={tablesAndTicketsTableStyle.modalNotify}
      />
    </Box>
  );
};

export default UnpaidTablesModal;
